import { Actions, createEffect, CreateEffectMetadata, ofType } from '@ngrx/effects';
import { createAction, on, props } from '@ngrx/store';
import { ActionCreator, ActionCreatorProps, NotAllowedCheck, TypedAction } from '@ngrx/store/src/models';
import { ReducerTypes } from '@ngrx/store/src/reducer_creator';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { apiErrorMessage } from '../shared/functions/general-api-error-handling';

interface ActionFailProps<PFailExtra = {}> {
  errorMessage: string;
  extras?: PFailExtra;
}

type SuccessActionType<T extends string> = `${T} success`;
type FailActionType<T extends string> = `${T} fail`;
type NoProps = false;

type InferredActionType<T extends string, P extends object | NoProps> = P extends object
  ? ((props: P & NotAllowedCheck<P>) => P & TypedAction<T>) & TypedAction<T>
  : ActionCreator<T, () => TypedAction<T>>;

export class CxbActionTriplet<
  TSource extends string,
  PSource extends object | NoProps,
  PSuccess extends object,
  PFailExtra extends object = {}
> {
  private constructor(
    public readonly sourceAction: InferredActionType<TSource, PSource>,
    public readonly successAction: ((props: PSuccess & NotAllowedCheck<PSuccess>) => PSuccess & TypedAction<SuccessActionType<TSource>>) &
      TypedAction<SuccessActionType<TSource>>,
    public readonly failAction: ((
      props: ActionFailProps<PFailExtra> & NotAllowedCheck<ActionFailProps<PFailExtra>>
    ) => ActionFailProps<PFailExtra> & TypedAction<FailActionType<TSource>>) &
      TypedAction<FailActionType<TSource>>
  ) {}

  public static withSuccessProps<T extends string, P extends object>(
    type: T,
    successProps: ActionCreatorProps<P> & NotAllowedCheck<P>
  ): CxbActionTriplet<T, NoProps, P> {
    return new CxbActionTriplet<T, NoProps, P>(
      createAction(type),
      createAction(`${type} success`, successProps),
      createAction(`${type} fail`, props<{ errorMessage: string }>())
    );
  }

  public static withSourceAndSuccessProps<T extends string, PSource extends object, PSuccess extends object>(
    type: T,
    sourceProps: ActionCreatorProps<PSource> & NotAllowedCheck<PSource>,
    successProps: ActionCreatorProps<PSuccess> & NotAllowedCheck<PSuccess>
  ): CxbActionTriplet<T, PSource, PSuccess> {
    return new CxbActionTriplet<T, PSource, PSuccess>(
      // @ts-ignore Can't yet figure out the problem here
      createAction(type, sourceProps),
      createAction(`${type} success`, successProps),
      createAction(`${type} fail`, props<{ errorMessage: string }>())
    );
  }

  public static withAllProps<T extends string, PSource extends object, PSuccess extends object, PFailExtra extends object>(
    type: T,
    sourceProps: ActionCreatorProps<PSource> & NotAllowedCheck<PSource>,
    successProps: ActionCreatorProps<PSuccess> & NotAllowedCheck<PSuccess>,
    failProps: ActionCreatorProps<ActionFailProps<PFailExtra>> & NotAllowedCheck<ActionFailProps<PFailExtra>>
  ): CxbActionTriplet<T, PSource, PSuccess, PFailExtra> {
    return new CxbActionTriplet<T, PSource, PSuccess, PFailExtra>(
      // @ts-ignore Can't yet figure out the problem here
      createAction(type, sourceProps),
      createAction(`${type} success`, successProps),
      createAction(`${type} fail`, failProps)
    );
  }

  public reduce<State extends { loading: boolean }>(
    successReducer?: (state: State, action: PSuccess & { type: SuccessActionType<TSource> }) => Omit<State, 'loading'>,
    sourceReducer?: (state: State, action: PSource & { type: TSource }) => Omit<State, 'loading'>
  ): ReducerTypes<State, readonly ActionCreator[]>[] {
    const _successReducer = successReducer
      ? (state: State, action: PSuccess & { type: SuccessActionType<TSource> }) => ({
          ...successReducer(state, action),
          loading: false,
        })
      : (state: State) => ({ ...state, loading: false });

    const _sourceReducer = sourceReducer
      ? (state: State, action: PSource & { type: TSource }) => ({ ...sourceReducer(state, action), loading: true })
      : (state: State) => ({ ...state, loading: true });

    return [
      // @ts-ignore
      on(this.sourceAction, _sourceReducer),
      // @ts-ignore
      on(this.failAction, (state: State) => ({ ...state, loading: false })),
      // @ts-ignore
      on(this.successAction, _successReducer),
    ];
  }

  public createEffect$<S>(
    actions$: Actions,
    successResultProject: (value: PSource) => Observable<PSuccess>,
    failExtrasProvider?: (action: PSource) => Observable<PFailExtra>
  ): Observable<(PSuccess & { type: SuccessActionType<TSource> }) | (ActionFailProps & FailActionType<TSource>)> & CreateEffectMetadata {
    // @ts-ignore
    return createEffect(() =>
      actions$.pipe(
        ofType((() => this.sourceAction)()),
        // @ts-ignore
        switchMap((action: PSource) =>
          successResultProject(action).pipe(
            // @ts-ignore
            map((result: PSuccess) => this.successAction(result)),
            catchError((error) => {
              console.error(error);
              return failExtrasProvider
                ? failExtrasProvider(action).pipe(map((result) => this.failAction({ ...apiErrorMessage(error), ...result })))
                : of(this.failAction(apiErrorMessage(error)));
            })
          )
        )
      )
    );
  }

  public createNonDispatchingEffect$<S>(
    actions$: Actions,
    project: (value: PSource) => Observable<any>,
    failExtrasProvider?: (action: PSource) => Observable<PFailExtra>
  ): Observable<any> & CreateEffectMetadata {
    // @ts-ignore
    return createEffect(
      () =>
        // @ts-ignore
        actions$.pipe(
          ofType((() => this.sourceAction)()),
          // @ts-ignore
          switchMap((action: PSource) =>
            project(action).pipe(
              // eslint-disable-next-line sonarjs/no-identical-functions
              catchError((error) => {
                return failExtrasProvider
                  ? failExtrasProvider(action).pipe(map((result) => this.failAction({ ...apiErrorMessage(error), ...result })))
                  : of(this.failAction(apiErrorMessage(error)));
              })
            )
          )
        ),
      { dispatch: false }
    );
  }
}
